import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CardActions, Switch, FormControlLabel } from '@mui/material';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import { ResponsiveChoropleth, ChoroplethProps } from '@nivo/geo';
import React from 'react';

import { ChoroplethDatum } from './_type';
import { worldFeatures } from './world-features';

import Arranger from '~c/components/base/arranger';
import Card from '~c/components/base/card';
import { DesignToken } from '~c/components/base/core';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import { useWindowSize } from '~c/hooks/use-hooks';

// Choropleth gradient (light to dark) per design system
const DEFAULT_COLORS = [
  '#afddea', // breeze
  '#8fc4d3', // ocean
  '#45879a', // deepSea
  DesignToken.brand.primary, // deepDown
];
const MIN_GRAPH_HEIGHT = 460;

interface JurisdictionChoroplethData {
  data: ChoroplethDatum[];
  maxValue: number;
  totalPolicies: number;
}

interface Props {
  className?: string;
  title?: string;
  subHeader?: React.ReactNode;
  jurisdictionData?: JurisdictionChoroplethData;
  projectJurisdictionData?: JurisdictionChoroplethData;
  chartTheme?: any;
  [key: string]: any;
}

const JurisdictionChoroplethGraph: React.FC<Props> = ({
  className,
  title,
  subHeader,
  jurisdictionData,
  projectJurisdictionData,
  chartTheme,
  ...props
}: Props) => {
  const theme = useTheme();
  const { height } = useWindowSize();
  const choroplethHeight = Math.max(MIN_GRAPH_HEIGHT, height / 3);

  const [showProjectLevel, setShowProjectLevel] = React.useState(false);

  const activeJurisdictionData = React.useMemo(() => {
    if (showProjectLevel) {
      return projectJurisdictionData?.data ?? [];
    } else {
      return jurisdictionData?.data ?? [];
    }
  }, [jurisdictionData, projectJurisdictionData, showProjectLevel]);

  // If no data or all values are 0, show empty state
  const noValue =
    !activeJurisdictionData ||
    activeJurisdictionData.length === 0 ||
    activeJurisdictionData.every(({ value }) => !value);

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowProjectLevel(event.target.checked);
  };

  return (
    <div className={className} style={{ height: '610px' }}>
      <Card title={title} subHeader={subHeader} style={{ height: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <a
            href="https://www.google.com/maps/d/viewer?mid=1grbvr9Ic-qJ-LTC9DHqpdzi2M-mtxl4&ll=16.30891111219782%2C0&z=2"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            AI Regulation Tracker
            <OpenInNewIcon style={{ fontSize: '14px' }} />
          </a>
          <FormControlLabel
            control={
              <Switch
                checked={showProjectLevel}
                onChange={handleToggleChange}
                color="primary"
                size="small"
              />
            }
            label={
              <span
                style={{
                  fontSize: '12px',
                  color: theme.palette.text.secondary,
                }}
              >
                Applied Policies
              </span>
            }
          />
        </div>

        <Arranger
          className="jurisdiction-choropleth-graph"
          alignItems="center"
          justify="center"
          sx={{ height: choroplethHeight, maxHeight: '460px' }}
        >
          {!noValue && worldFeatures.length > 0 ? (
            <ResponsiveChoropleth
              data={activeJurisdictionData}
              features={worldFeatures as any}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              colors={DEFAULT_COLORS}
              domain={[
                0,
                Math.max(...activeJurisdictionData.map((d) => d.value)),
              ]}
              unknownColor={DesignToken.textMuted}
              label="properties.name"
              valueFormat=".0f"
              enableGraticule
              graticuleLineColor={theme.palette.divider}
              borderWidth={0.5}
              borderColor={DesignToken.textPrimary}
              projectionScale={100}
              projectionTranslation={[0.5, 0.6]}
              theme={chartTheme}
              isInteractive
              tooltip={({ feature }: { feature: any }) => {
                return (
                  <div
                    style={{
                      background: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      padding: '9px 12px',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: `${DesignToken.radiusSmall}px`,
                      boxShadow: theme.shadows[2],
                    }}
                  >
                    <strong>{feature.properties?.name || feature.id}</strong>
                    <br />
                    {showProjectLevel
                      ? 'Applied Policies: '
                      : 'Available Policies: '}
                    {feature.data?.value?.toLocaleString() ||
                      feature.value?.toLocaleString() ||
                      '0'}
                  </div>
                );
              }}
              {...props}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: theme.palette.text.secondary,
                fontSize: '16px',
              }}
            >
              No jurisdiction data available
            </div>
          )}
        </Arranger>
        <CardActions className="legend-container">
          <Arranger justify="space-around">
            {activeJurisdictionData && activeJurisdictionData.length > 0 && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Low
                </span>
                <div style={{ display: 'flex', height: '20px' }}>
                  {DEFAULT_COLORS.map((color, index) => (
                    <div
                      key={index}
                      style={{
                        width: '20px',
                        height: '100%',
                        backgroundColor: color,
                      }}
                    />
                  ))}
                </div>
                <span
                  style={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  High
                </span>
              </div>
            )}
          </Arranger>
        </CardActions>
      </Card>
    </div>
  );
};

export default muiStyled(JurisdictionChoroplethGraph)(({ theme: muiTheme }) => {
  return css`
    .legend-container {
      padding: ${muiTheme.spacing(spc.m)} 0 0 0;

      /* The legend bar take up full width */
      > div {
        flex-grow: 1;
      }
    }
  `;
});
